import {
  useState,
  useRef,
  useEffect,
  useCallback,
  createContext,
  useContext,
  type FC,
} from "react";
import {
  ActionBarPrimitive,
  AuiIf,
  ErrorPrimitive,
  MessagePartPrimitive,
  MessagePrimitive,
  ThreadPrimitive,
  useAuiState,
} from "@assistant-ui/react";
import { AlertCircle, Bug, CheckIcon, CopyIcon, DownloadIcon } from "lucide-react";
import { DocumentModal } from "~/components/DocumentModal";
import { Composer, type ComposerProps } from "~/components/assistant-v2/Composer";
import { CreatePlanMessage } from "~/components/assistant-v2/CreatePlanMessage";
import { DefaultToolMessage } from "~/components/assistant-v2/DefaultToolMessage";
import { FinalResultMessage } from "~/components/assistant-v2/FinalResultMessage";
import { MarkdownText } from "~/components/assistant-v2/MarkdownText";
import { MessageFeedbackInput } from "~/components/assistant-v2/MessageFeedback";
import { ToolCallGroup } from "~/components/assistant-v2/ToolCallGroup";
import { TooltipIconButton } from "~/components/assistant-v2/TooltipIconButton";
import type { ContextBuffer } from "~/components/assistant-v2/useContextBuffer";
import { useAssistantContext } from "~/components/assistant/AssistantContext";
import { CopyButton } from "~/components/assistant/CopyButton";
import { ShimmerText } from "~/components/assistant/ShimmerText";
import {
  ANALYSIS_AGENT_USAGE_GUIDE_PATH,
  ANALYSIS_AGENT_USAGE_GUIDE_TITLE,
} from "~/components/assistant/constants";
import { CancellationMessage } from "~/components/assistant/messages/CancellationMessage";
import type { AssistantMessageCustomMetadata, MessageFeedback } from "~/components/assistant/types";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { cn } from "~/lib/utils";

/**
 * Context providing the backend message ID for the *current* streaming turn.
 * Historical turns get their IDs from user message metadata instead.
 */
const StreamingMessageIdContext = createContext<string | null>(null);

/** Extract typed custom metadata from an assistant-ui message. */
function getCustomMetadata(message: {
  metadata?: unknown;
}): AssistantMessageCustomMetadata | undefined {
  return (message.metadata as { custom?: AssistantMessageCustomMetadata } | undefined)?.custom;
}

export interface ThreadProps extends ComposerProps {
  suggestions?: Array<{ title: string; prompt: string }>;
  contextBuffer?: ContextBuffer | null;
  isLoadingHistory?: boolean;
  runError?: string | null;
  /** Backend message ID for the current streaming turn (from `user_message_persisted` event). */
  streamingFeedbackMessageId?: string | null;
}

export const Thread: FC<ThreadProps> = ({
  suggestions,
  contextBuffer,
  isLoadingHistory,
  runError,
  streamingFeedbackMessageId,
  ...composerProps
}) => {
  return (
    <StreamingMessageIdContext.Provider value={streamingFeedbackMessageId ?? null}>
      <ThreadPrimitive.Root
        className="aui-root aui-thread-root @container flex h-full flex-col bg-background"
        style={{
          ["--thread-max-width" as string]: "56rem",
        }}
      >
        <ThreadPrimitive.Viewport
          turnAnchor="top"
          autoScroll
          className="aui-thread-viewport relative flex flex-1 flex-col overflow-x-auto overflow-y-auto scroll-smooth px-4 pt-4 scrollbar-subtle"
        >
          {isLoadingHistory ? (
            <MessageSkeletons />
          ) : (
            <AuiIf condition={({ thread }) => thread.isEmpty}>
              <ThreadWelcome suggestions={suggestions} />
            </AuiIf>
          )}

          <ThreadPrimitive.Messages
            components={{
              UserMessage,
              AssistantMessage,
            }}
          />

          <ThreadPrimitive.ViewportFooter className="aui-thread-viewport-footer sticky bottom-0 mx-auto mt-auto flex w-full max-w-(--thread-max-width) flex-col gap-2 overflow-visible rounded-t-3xl bg-background">
            {runError && <RunError message={runError} />}
            <div className="relative mx-auto w-full max-w-(--thread-max-width)">
              <div className="relative z-20">
                <Composer {...composerProps} contextBuffer={contextBuffer} />
              </div>
              <CancellingBanner isVisible={composerProps.isCancelling === true} />
            </div>
            <div className="text-center text-xs text-gray-500 pb-2">
              AI can make mistakes. Review for accuracy.
            </div>
          </ThreadPrimitive.ViewportFooter>
        </ThreadPrimitive.Viewport>
      </ThreadPrimitive.Root>
    </StreamingMessageIdContext.Provider>
  );
};

const MessageSkeletons: FC = () => {
  return (
    <div className="mx-auto w-full max-w-(--thread-max-width) flex flex-col gap-6 py-4 animate-pulse">
      {[0, 1, 2].map((turn) => (
        <div key={turn} className="flex flex-col gap-4">
          <div className="flex justify-end px-2">
            <div className="h-10 w-2/5 rounded-2xl bg-muted" />
          </div>
          <div className="flex flex-col gap-2 px-2">
            <div className="h-4 w-3/4 rounded bg-muted" />
            <div className="h-4 w-1/2 rounded bg-muted" />
            <div className="h-4 w-5/6 rounded bg-muted" />
          </div>
        </div>
      ))}
    </div>
  );
};

const SuggestionButton: FC<{
  ariaLabel: string;
  title: string;
  onClick?: () => void;
}> = ({ ariaLabel, title, onClick }) => (
  <Button
    variant="ghost"
    onClick={onClick}
    className="aui-thread-welcome-suggestion h-auto w-full items-start justify-start overflow-hidden rounded-xl border px-3 py-2 text-left text-sm transition-colors hover:bg-muted"
    aria-label={ariaLabel}
  >
    <span className="aui-thread-welcome-suggestion-text-1 block w-full truncate font-medium">
      {title}
    </span>
  </Button>
);

interface ThreadWelcomeProps {
  suggestions?: Array<{ title: string; prompt: string }>;
}

const ThreadWelcome: FC<ThreadWelcomeProps> = ({ suggestions }) => {
  const [showUsageGuide, setShowUsageGuide] = useState(false);

  return (
    <div className="aui-thread-welcome-root mx-auto my-auto flex w-full max-w-(--thread-max-width) grow flex-col">
      <div className="flex w-full flex-1 flex-col justify-center px-4">
        <Badge
          className={cn(
            "mb-3 w-fit rounded-[6px] text-primary",
            "px-1.5 py-0.5 text-[10px] font-semibold tracking-wide uppercase"
          )}
        >
          Beta
        </Badge>
        <h1 className="text-xl font-semibold leading-tight">Analysis Agent</h1>
        <p className="my-2 text-sm leading-relaxed text-gray-600">
          Ask questions, generate tables or charts, and compare data across tables.{" "}
          <button
            onClick={() => setShowUsageGuide(true)}
            className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline cursor-pointer"
          >
            View Usage Guide
          </button>
        </p>
      </div>
      <ThreadSuggestions suggestions={suggestions} />
      <DocumentModal
        open={showUsageGuide}
        onClose={() => setShowUsageGuide(false)}
        title={ANALYSIS_AGENT_USAGE_GUIDE_TITLE}
        markdownPath={ANALYSIS_AGENT_USAGE_GUIDE_PATH}
      />
    </div>
  );
};

const ThreadSuggestions: FC<{ suggestions?: Array<{ title: string; prompt: string }> }> = ({
  suggestions,
}) => {
  return (
    <div className="aui-thread-welcome-suggestions grid w-full gap-2 pb-4 @md:grid-cols-2">
      {suggestions?.map((suggestion, index) => (
        <div
          key={suggestion.prompt}
          className="aui-thread-welcome-suggestion-display min-w-0 fade-in slide-in-from-bottom-2 animate-in fill-mode-both duration-200"
          style={{ animationDelay: `${100 + index * 50}ms` }}
        >
          <ThreadPrimitive.Suggestion prompt={suggestion.prompt} asChild>
            <SuggestionButton ariaLabel={suggestion.prompt} title={suggestion.title} />
          </ThreadPrimitive.Suggestion>
        </div>
      ))}
    </div>
  );
};

const MessageError: FC = () => {
  return (
    <MessagePrimitive.Error>
      <ErrorPrimitive.Root className="aui-message-error-root mt-2 rounded-md border border-destructive bg-destructive/10 p-3 text-destructive text-sm dark:bg-destructive/5 dark:text-red-200">
        <ErrorPrimitive.Message className="aui-message-error-message line-clamp-2" />
      </ErrorPrimitive.Root>
    </MessagePrimitive.Error>
  );
};

const CancellingBanner: FC<{ isVisible: boolean }> = ({ isVisible }) => {
  return (
    <div
      className={cn(
        "pointer-events-none absolute inset-x-4 bottom-[calc(100%)] z-10",
        "transform-gpu transition-all duration-200 ease-out",
        isVisible ? "translate-y-0 opacity-100" : "translate-y-2 opacity-0"
      )}
      aria-hidden={!isVisible}
    >
      <div className="mx-auto flex w-fit items-center justify-center gap-2 rounded-t-md bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-500 shadow-sm">
        <div className="size-3 animate-spin rounded-full border-1 border-current border-t-transparent" />
        Cancelling...
      </div>
    </div>
  );
};

const RunError: FC<{ message: string }> = ({ message }) => {
  return (
    <div className="mx-auto w-full max-w-(--thread-max-width) px-2 py-3">
      <div className="flex items-start gap-3 rounded-md border border-destructive bg-destructive/10 p-3 text-destructive text-sm dark:bg-destructive/5 dark:text-red-200">
        <AlertCircle className="mt-0.5 size-4 shrink-0" />
        <span>{message}</span>
      </div>
    </div>
  );
};

/**
 * TODO: The backend agent currently emits responses TWICE in the AG-UI event stream:
 *   1. As raw TEXT_MESSAGE events (streamed incrementally)
 *   2. As a `final_result` tool call containing the same content
 *
 * The agent literally says "I see the issue - I need to use the `final_result` tool"
 * mid-stream and re-emits its response via the tool call. The AG-UI runtime aggregates
 * both into a single assistant message with multiple parts (text + tool call), causing
 * the response to render twice.
 *
 * This wrapper suppresses text parts when a `final_result` tool call is present,
 * treating the raw text as intermediate reasoning. The proper fix is to update the
 * backend agent to ONLY emit `final_result` tool calls and not duplicate as raw text.
 */
const TextWithFinalResultSuppression: FC = () => {
  const message = useAuiState((s) => s.message);
  const part = useAuiState((s) => s.part);

  const hasFinalResult = message.content?.some(
    (p: { type: string; toolName?: string }) =>
      p.type === "tool-call" && p.toolName === "final_result"
  );
  if (hasFinalResult) {
    return null;
  }

  const hasToolCalls = message.content?.some((p: { type: string }) => p.type === "tool-call");
  const hasRunningTools = message.content?.some(
    (p: { type: string; result?: unknown }) => p.type === "tool-call" && p.result == null
  );

  // Text in a message with tool calls is transient "thinking" narration.
  // Show only the latest text part: as shimmer while tools run, then as a
  // loader between tool calls while the message is still streaming.
  if (hasToolCalls) {
    const textParts = message.content?.filter((p: { type: string }) => p.type === "text") as
      | Array<{ type: "text"; text: string }>
      | undefined;
    const lastText = textParts?.[textParts.length - 1]?.text;
    const isLastTextPart = part.type === "text" && (part as { text: string }).text === lastText;

    if (!isLastTextPart) {
      return null;
    }

    if (hasRunningTools) {
      return (
        <ShimmerText
          className="text-sm"
          style={
            {
              "--base-color": "var(--gray-500)",
              "--shimmer-color": "var(--gray-800)",
            } as React.CSSProperties
          }
        >
          {(part as { text: string }).text}
        </ShimmerText>
      );
    }

    // Tools completed but message still streaming — show loader
    return (
      <MessagePartPrimitive.InProgress>
        <span className="animate-pulse ml-0.5" style={{ fontFamily: "revert" }}>
          {" ●"}
        </span>
      </MessagePartPrimitive.InProgress>
    );
  }

  // Pure text response (no tool calls) — render as markdown.
  return (
    <>
      <MarkdownText />
      {!hasRunningTools && (
        <MessagePartPrimitive.InProgress>
          <span className="animate-pulse ml-0.5" style={{ fontFamily: "revert" }}>
            {" ●"}
          </span>
        </MessagePartPrimitive.InProgress>
      )}
    </>
  );
};

const AssistantMessage: FC = () => {
  const message = useAuiState((s) => s.message);
  const showCancelled = getCustomMetadata(message)?.cancelled === true;

  return (
    <MessagePrimitive.Root
      className="aui-assistant-message-root fade-in slide-in-from-bottom-1 relative mx-auto w-full max-w-(--thread-max-width) animate-in py-3 duration-150"
      data-role="assistant"
    >
      <div className="aui-assistant-message-content wrap-break-word px-2 text-foreground leading-relaxed text-[14px]">
        <MessagePrimitive.Parts
          components={{
            Text: TextWithFinalResultSuppression,
            ToolGroup: ToolCallGroup,
            tools: {
              by_name: {
                final_result: FinalResultMessage,
                create_plan: CreatePlanMessage,
              },
              Fallback: DefaultToolMessage,
            },
          }}
        />

        {showCancelled && <CancellationMessage />}
        <MessageError />
      </div>

      <div className="aui-assistant-message-footer mt-1 ml-2 flex">
        <AssistantActionBar />
      </div>
    </MessagePrimitive.Root>
  );
};

/**
 * Resolves the backend message ID and existing feedback for the user query
 * that this assistant message is responding to.
 *
 * For history-loaded messages, this data lives on the assistant message's
 * own metadata (set during history conversion). For the current streaming
 * turn, it falls back to the streaming context.
 */
function useUserFeedbackInfo() {
  const streamingMessageId = useContext(StreamingMessageIdContext);
  const message = useAuiState((s) => s.message);
  const isLast = message.isLast;

  // History-loaded messages carry the backend ID in metadata.custom
  const custom = getCustomMetadata(message);
  if (custom?.backendMessageId) {
    return { backendMessageId: custom.backendMessageId, feedback: custom.feedback ?? null };
  }

  // Fallback: current streaming turn — only applies to the last message
  if (isLast && streamingMessageId) {
    return { backendMessageId: streamingMessageId, feedback: null };
  }

  return null;
}

const AssistantActionBar: FC = () => {
  const { api, conversationId } = useAssistantContext();
  const info = useUserFeedbackInfo();

  const [feedback, setFeedback] = useState<MessageFeedback | null>(info?.feedback ?? null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Sync with history-loaded feedback when info changes (e.g. conversation switch)
  useEffect(() => {
    setFeedback(info?.feedback ?? null);
  }, [info?.feedback]);

  const handleSubmit = useCallback(
    async (value: 0 | 1, text?: string | null) => {
      if (!conversationId || !info?.backendMessageId || isSubmitting) return;
      setIsSubmitting(true);
      try {
        const result = await api.putMessageFeedback(conversationId, info.backendMessageId, {
          feedback_type: "BINARY",
          feedback_value: value,
          text: text ?? null,
        });
        setFeedback(result);
      } catch (error) {
        console.error("Failed to submit feedback:", error);
      } finally {
        setIsSubmitting(false);
      }
    },
    [api, conversationId, info?.backendMessageId, isSubmitting]
  );

  const handleClear = useCallback(async () => {
    if (!conversationId || !info?.backendMessageId || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await api.deleteMessageFeedback(conversationId, info.backendMessageId);
      setFeedback(null);
    } catch (error) {
      console.error("Failed to clear feedback:", error);
    } finally {
      setIsSubmitting(false);
    }
  }, [api, conversationId, info?.backendMessageId, isSubmitting]);

  return (
    <ActionBarPrimitive.Root
      hideWhenRunning
      autohide="not-last"
      autohideFloat="single-branch"
      className="aui-assistant-action-bar-root col-start-3 row-start-2 -ml-1 flex gap-1 text-muted-foreground data-floating:absolute data-floating:rounded-md data-floating:border data-floating:bg-background data-floating:p-1 data-floating:shadow-sm"
    >
      <ActionBarPrimitive.Copy asChild>
        <TooltipIconButton tooltip="Copy">
          <AuiIf condition={({ message }) => message.isCopied}>
            <CheckIcon />
          </AuiIf>
          <AuiIf condition={({ message }) => !message.isCopied}>
            <CopyIcon />
          </AuiIf>
        </TooltipIconButton>
      </ActionBarPrimitive.Copy>
      <ActionBarPrimitive.ExportMarkdown asChild>
        <TooltipIconButton tooltip="Export as Markdown">
          <DownloadIcon />
        </TooltipIconButton>
      </ActionBarPrimitive.ExportMarkdown>
      {info?.backendMessageId && (
        <MessageFeedbackInput
          feedback={feedback}
          isSubmitting={isSubmitting}
          onSubmit={handleSubmit}
          onClear={handleClear}
        />
      )}
    </ActionBarPrimitive.Root>
  );
};

function TraceId({ traceId, debugMode }: { traceId?: string; debugMode: boolean }) {
  if (!debugMode || !traceId) {
    return null;
  }

  return (
    <div className="sticky top-0 self-start" onClick={(e) => e.stopPropagation()}>
      <CopyButton
        text={traceId}
        icon={Bug}
        tooltipText={`Copy Trace ID: ${traceId}`}
        variant="light"
        size="sm"
      />
    </div>
  );
}

/** ~3 lines at 14px font / relaxed leading */
const USER_MSG_COLLAPSED_HEIGHT = 70;

const UserMessage: FC = () => {
  const { debugMode = false } = useAssistantContext();
  const message = useAuiState((s) => s.message);
  const traceId = (message.metadata as Record<string, unknown> | undefined)?.traceId as
    | string
    | undefined;

  const contentRef = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) {
      return;
    }
    const check = () => setIsOverflowing(el.scrollHeight > USER_MSG_COLLAPSED_HEIGHT);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const collapse = useCallback(() => {
    setIsExpanded(false);
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  }, []);

  useEffect(() => {
    if (!isExpanded) {
      return;
    }
    function handleClick(e: MouseEvent) {
      if (contentRef.current && !contentRef.current.contains(e.target as Node)) {
        collapse();
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [isExpanded, collapse]);

  const expand = useCallback(() => {
    if (isOverflowing && !isExpanded) {
      setIsExpanded(true);
    }
  }, [isOverflowing, isExpanded]);

  return (
    <MessagePrimitive.Root
      className="aui-user-message-root fade-in slide-in-from-bottom-1 mx-auto grid w-full max-w-(--thread-max-width) animate-in auto-rows-auto grid-cols-[minmax(72px,1fr)_auto] content-start gap-y-2 px-2 py-3 duration-150 [&:where(>*)]:col-start-2"
      data-role="user"
    >
      <div className="aui-user-message-content-wrapper relative col-start-2 min-w-0">
        <div
          ref={contentRef}
          role={isOverflowing ? "button" : undefined}
          tabIndex={isOverflowing ? 0 : undefined}
          onClick={expand}
          onKeyDown={
            isOverflowing && !isExpanded ? (e) => e.key === "Enter" && expand() : undefined
          }
          className={cn(
            "aui-user-message-content wrap-break-word rounded-2xl bg-muted px-4 py-2.5 text-foreground text-[14px] relative grid w-full items-start",
            isOverflowing && !isExpanded && "cursor-pointer",
            debugMode && traceId ? "grid-cols-[1fr_30px] gap-1" : "grid-cols-[1fr]"
          )}
          style={{
            maxHeight: isExpanded ? "20vh" : `${USER_MSG_COLLAPSED_HEIGHT}px`,
            overflowY: isExpanded ? "auto" : "hidden",
          }}
        >
          <MessagePrimitive.Parts />
          <TraceId traceId={traceId} debugMode={debugMode} />
          {isOverflowing && !isExpanded && (
            <div
              className="absolute bottom-0 left-0 right-0 h-8 rounded-b-2xl pointer-events-none bg-muted"
              style={{
                maskImage: "linear-gradient(to bottom, transparent, black)",
                WebkitMaskImage: "linear-gradient(to bottom, transparent, black)",
              }}
            />
          )}
        </div>
      </div>
    </MessagePrimitive.Root>
  );
};
